import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const YEARS_PER_PAGE = 12;

const parseISO = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDisplay = (date) =>
  date.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });

export default function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  minDate,
  maxDate,
  className = "",
  disabled = false,
}) {
  const selected = parseISO(value);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(selected || new Date());
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  // "days" | "months" | "years" — clicking the header title drills into a
  // faster picker so you don't have to click prev/next dozens of times
  // (e.g. picking a birth year like 2002).
  const [view, setView] = useState("days");

  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    setCoords({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (!open) {
      updatePosition();
      setView("days");
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const clickedBtn = wrapperRef.current?.contains(e.target);
      const clickedPanel = panelRef.current?.contains(e.target);
      if (!clickedBtn && !clickedPanel) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === "Escape") setOpen(false); };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => { if (selected) setViewDate(selected); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setView("days"); }, [open]);

  const min = minDate ? parseISO(minDate) : null;
  const max = maxDate ? parseISO(maxDate) : null;

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  // ---- navigation (prev/next behaves differently per view) ----
  const goPrev = () => {
    if (view === "days") setViewDate(new Date(year, month - 1, 1));
    else if (view === "months") setViewDate(new Date(year - 1, month, 1));
    else setViewDate(new Date(year - YEARS_PER_PAGE, month, 1));
  };
  const goNext = () => {
    if (view === "days") setViewDate(new Date(year, month + 1, 1));
    else if (view === "months") setViewDate(new Date(year + 1, month, 1));
    else setViewDate(new Date(year + YEARS_PER_PAGE, month, 1));
  };

  // ---- days grid ----
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: daysInPrevMonth - i, other: true, offset: -1 });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, other: false, offset: 0 });
  while (cells.length % 7 !== 0) cells.push({ day: cells.length - firstDay - daysInMonth + 1, other: true, offset: 1 });

  const dateFor = (cell) => new Date(year, month + cell.offset, cell.day);

  const isDisabled = (date) => (min && date < min) || (max && date > max);
  const isToday = (date) => {
    const t = new Date();
    return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
  };
  const isSelected = (date) =>
    selected &&
    date.getDate() === selected.getDate() &&
    date.getMonth() === selected.getMonth() &&
    date.getFullYear() === selected.getFullYear();

  const pick = (cell) => {
    const date = dateFor(cell);
    if (isDisabled(date)) return;
    if (cell.other) setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
    onChange(toISO(date));
    setOpen(false);
  };

  const goToday = () => {
    const t = new Date();
    if ((min && t < min) || (max && t > max)) return;
    setViewDate(t);
    onChange(toISO(t));
    setOpen(false);
  };

  // ---- months grid (12 months of viewDate's year) ----
  const isMonthDisabled = (m) => {
    const monthStart = new Date(year, m, 1);
    const monthEnd = new Date(year, m + 1, 0);
    if (min && monthEnd < min) return true;
    if (max && monthStart > max) return true;
    return false;
  };
  const pickMonth = (m) => {
    if (isMonthDisabled(m)) return;
    setViewDate(new Date(year, m, 1));
    setView("days");
  };

  // ---- years grid (page of YEARS_PER_PAGE years) ----
  const pageStart = year - (year % YEARS_PER_PAGE);
  const yearCells = Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i);
  const isYearDisabled = (y) => {
    const yearStart = new Date(y, 0, 1);
    const yearEnd = new Date(y, 11, 31);
    if (min && yearEnd < min) return true;
    if (max && yearStart > max) return true;
    return false;
  };
  const pickYear = (y) => {
    if (isYearDisabled(y)) return;
    setViewDate(new Date(y, month, 1));
    setView("months");
  };

  const headerLabel =
    view === "days"
      ? (
        <>
          <span className="date-picker-title-part" onClick={() => setView("months")}>
            {MONTH_NAMES[month]}
          </span>{" "}
          <span className="date-picker-title-part" onClick={() => setView("years")}>
            {year}
          </span>
        </>
      )
      : view === "months"
      ? (
        <span className="date-picker-title-part" onClick={() => setView("years")}>
          {year}
        </span>
      )
      : `${pageStart} - ${pageStart + YEARS_PER_PAGE - 1}`;

  return (
    <div className="date-picker-wrap" ref={wrapperRef}>
      <button
        type="button"
        ref={btnRef}
        className={`date-picker-btn ${className}`}
        disabled={disabled}
        onClick={handleToggle}
      >
        <FaCalendarAlt className="date-picker-icon" />
        {selected ? <span>{formatDisplay(selected)}</span> : <span className="date-picker-placeholder">{placeholder}</span>}
      </button>

      {open &&
        createPortal(
          <div
            className="date-picker-panel"
            ref={panelRef}
            style={{ position: "absolute", top: coords.top, left: coords.left }}
          >
            <div className="date-picker-header">
              <button type="button" className="date-picker-nav-btn" onClick={goPrev}>
                <FaChevronLeft />
              </button>
              <span className="date-picker-title">{headerLabel}</span>
              <button type="button" className="date-picker-nav-btn" onClick={goNext}>
                <FaChevronRight />
              </button>
            </div>

            {view === "days" && (
              <>
                <div className="date-picker-weekdays">
                  {DAY_NAMES.map((d) => <span key={d} className="date-picker-weekday">{d}</span>)}
                </div>

                <div className="date-picker-grid">
                  {cells.map((cell, i) => {
                    const date = dateFor(cell);
                    const disabledDay = isDisabled(date);
                    return (
                      <span
                        key={i}
                        className={`date-picker-day ${cell.other ? "other-month" : ""} ${isToday(date) ? "today" : ""} ${isSelected(date) ? "selected" : ""} ${disabledDay ? "disabled" : ""}`}
                        onClick={() => pick(cell)}
                      >
                        {cell.day}
                      </span>
                    );
                  })}
                </div>

                <div className="date-picker-footer">
                  <button type="button" className="date-picker-today-btn" onClick={goToday}>
                    Today
                  </button>
                </div>
              </>
            )}

            {view === "months" && (
              <div className="date-picker-months-grid">
                {MONTH_SHORT.map((m, i) => (
                  <span
                    key={m}
                    className={`date-picker-month-cell ${i === month ? "selected" : ""} ${isMonthDisabled(i) ? "disabled" : ""}`}
                    onClick={() => pickMonth(i)}
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}

            {view === "years" && (
              <div className="date-picker-years-grid">
                {yearCells.map((y) => (
                  <span
                    key={y}
                    className={`date-picker-year-cell ${y === year ? "selected" : ""} ${isYearDisabled(y) ? "disabled" : ""}`}
                    onClick={() => pickYear(y)}
                  >
                    {y}
                  </span>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}